import constants from '@playcastdotio/GlobalConfig';
import { Button, buttonVariants } from '@playcastdotio/Shadcn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPersonRunningFast, faGearComplexCode, faVideoArrowUpRight } from '@fortawesome/pro-light-svg-icons';
import { Link } from 'react-router-dom';
import { cn } from '../../utility/base';
import { download, setAutoDownloadFlag } from '../../utility/api';
import { faWindows } from '@fortawesome/free-brands-svg-icons';
import ReactPlayer from 'react-player/youtube';
import { Helmet } from 'react-helmet-async';
import { isMobileDevice, isNonWindowsOS } from '../../utility/utility';
import { usePavilion } from '../contexts/PavilionContext';
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { sendAnalytics, PlaycastAnalyticsPageViewed } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../api';
import { authStore } from '../../api/stores/globalStores';
import { useNavigate } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import config from '../../config.json';

// Import new Aida components
import { AidaHero } from '../components/aida/AidaHero';
import { AidaFeaturedGame } from '../components/aida/AidaFeaturedGame';
import { AidaFeatureHighlights } from '../components/aida/AidaFeatureHighlights';
import { AidaFAQ } from '../components/aida/AidaFAQ';
import { AidaFinalCTA } from '../components/aida/AidaFinalCTA';
import { AidaFooter } from '../components/aida/AidaFooter';

import { HomeThreePlatformOptions } from '../components/aida/HomeThreePlatformOptions';
import { InstantPlayPillars } from '../components/aida/InstantPlayPillars';
import { TrustedPartnersReel } from '../components/aida/TrustedPartnersReel';
import { SecurityControl } from '../components/aida/SecurityControl';
import { FAQ } from '../components/aida/FAQ';
import { FinalCTA } from '../components/aida/FinalCTA';
import { RWBYCarousel } from '../components/aida/RWBYCarousel';
import { useAutoScaler } from '../../hooks/useAutoScaler';

/**
 * Generate Pavilion URL based on current environment
 */
const generatePavilionUrl = (): string => {
  return constants.PAVILION_PLAYER_BASE_URL;
};

const BannerImage = ({ src, alt, text, targetId }: { src: string; alt: string; text: string; targetId: string }) => {
  const [isLifted, setIsLifted] = useState(false);

  const handleClick = () => {
    setIsLifted((prev) => !prev);
  };

  const handleTextClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent banner toggle
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className='relative w-[140px] cursor-pointer overflow-hidden sm:w-[160px] md:w-[180px] lg:w-[220px]' onClick={handleClick}>
      {/* Text appears only after banner is lifted */}
      <div
        className={`font-inter absolute left-0 top-[15%] flex h-full w-full items-center justify-center px-2 text-center text-[12px] font-normal italic leading-[1.2] text-[#FFFEFA] transition-opacity duration-500 ease-in-out sm:text-[14px] md:text-[16px] lg:text-[18px] ${isLifted ? 'pointer-events-auto z-20 opacity-100' : 'pointer-events-none z-0 opacity-0'} `}
        onClick={handleTextClick}
      >
        {text}
      </div>

      {/* Isolated image wrapper to fix Chrome ghosting */}
      <div className='backface-hidden relative z-10 will-change-transform'>
        <motion.img
          src={src}
          alt={alt}
          className='block h-auto w-full object-cover'
          animate={{ y: isLifted ? '-50%' : '0%' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            transformStyle: 'preserve-3d',
          }}
        />
      </div>
    </div>
  );
};

export default function BannersSection() {
  const banners = [
    {
      src: '/Hosts-banner.svg',
      alt: 'Hosts-banner',
      text: 'Get Access to Free Games. Make Your Machine Work for You.',
      targetId: 'section-hosts',
    },
    {
      src: '/Streamers-banner.svg',
      alt: 'Streamers-banner',
      text: 'Your Steam, Upgraded to Real-Time Play',
      targetId: 'section-streamers',
    },
    {
      src: '/Gamers-banner.svg',
      alt: 'Gamers-banner',
      text: 'Play for Free. No Downloads. Play from Anywhere.',
      targetId: 'section-gamers',
    },
    {
      src: '/Developers-banner.svg',
      alt: 'Developers-banner',
      text: 'Promote your games virally across social media with no friction',
      targetId: 'section-devs',
    },
  ];

  return (
    <div
      className='w-full overflow-x-hidden pb-20'
      style={{
        backgroundColor: '#2C354C',
        backgroundImage: 'url(/Gibber-Texture.png)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className='mt-10 flex w-full flex-col items-center justify-center gap-0 lg:flex-row'>
        {/* Left column */}
        <div className='flex w-full flex-col items-center pb-10 lg:w-1/2'>
          <div className='h-0 w-screen shadow-md outline outline-[5px] outline-[#FFFEFA]' />
          <div className='mt-0 flex flex-row justify-center gap-4 sm:gap-6 lg:gap-20 xl:gap-40'>
            {banners.slice(0, 2).map((banner, index) => (
              <BannerImage key={index} {...banner} />
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className='flex w-full flex-col items-center pb-10 lg:w-1/2'>
          <div className='h-0 w-screen shadow-md outline outline-[5px] outline-[#FFFEFA]' />
          <div className='mt-0 flex flex-row justify-center gap-4 sm:gap-6 lg:gap-20 xl:gap-40'>
            {banners.slice(2).map((banner, index) => (
              <BannerImage key={index + 2} {...banner} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const images = [
  '/gangbeasts.jpg',
  '/horizon.jpg',
  '/tlou.jpg',
  '/subnautica.jpg',
  '/speedrunners.jpg',
  '/stickfight.jpg',
  '/castlecrashers.jpg',
  '/cuphead.jpg',
];

const structuredData = {
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'Playcast',
  url: 'https://playcast.io',
  logo: 'https://playcast.io/images/playcast_logo-96x96.png',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-800-555-5555',
      contactType: 'Customer Service',
      areaServed: 'US',
      availableLanguage: 'English',
    },
  ],
  sameAs: [
    'https://discord.gg/7PYGxU68WH',
    'https://www.youtube.com/channel/UC-k16u9Ea_dvRhB70f-h5lQ',
    'https://www.twitch.tv/playcastio',
    'https://www.tiktok.com/@playcastdotio',
    'https://www.instagram.com/playcastio/',
  ],
};

export const Home = () => {
  const { isPavilionMode } = usePavilion();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isContentReady } = useAutoScaler();
  const [activeDemoUrl, setActiveDemoUrl] = useState<string | null>(null);

  useEffect(() => {
    // Set initial auth state
    setIsAuthenticated(!!authStore.currentState.accessToken);

    // Listen for auth state changes
    const listener = authStore.addListener((state) => {
      setIsAuthenticated(!!state.accessToken);
    });

    return () => {
      authStore.removeListener(listener);
    };
  }, []);

  // Clean scroll system: wait for content, then scroll to hash or top
  useEffect(() => {
    // Prevent browser's default scroll restoration
    window.history.scrollRestoration = 'manual';
  }, []);

  // Once content is ready, perform the final scroll
  useEffect(() => {
    if (isContentReady) {
      const hash = window.location.hash;

      if (hash) {
        // Hash present: scroll to that section
        setTimeout(() => {
          const element = document.getElementById(hash.substring(1));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100); // Small delay to ensure rendering is complete
      } else {
        // No hash: stay at top
        //window.scrollTo(0, 0);
      }
    }
  }, [isContentReady]);

  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  const clonedImages = [...images, images[0]]; // clone first image at the end

  useEffect(() => {
    setIndex(1); // trigger first slide immediately
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => prev + 1);
    }, 6000); // still 12s per slide

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (index === clonedImages.length - 1) {
      // Reached cloned slide, snap back to index 0
      setTimeout(() => {
        setIsTransitioning(false); // disable animation
        setIndex(0); // jump to real first slide
      }, 50); // short delay after last transition
    } else {
      setIsTransitioning(true); // enable animation for normal slides
    }
  }, [index, clonedImages.length]);

  return isPavilionMode ? (
    <>
      <Helmet>
        <title>Playcast Pavilion - A First-of-its-Kind Marketplace for Play</title>
        <meta
          name='description'
          content='Playcast Pavilion is an app that joins your Windows Gaming PC to a globally-distributed, game-streaming service. Your favorite game developers provide you with free games in exchange for demos, hosting, and promotion by streamers and gamers. Join the Playcast Pavilion and get your games for free!'
        />
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>

        {/* Landing Greeting */}
        <div className='bg-content text-content-text m-0 h-auto w-full p-0'>
          {/* Landing Greeting */}
          <div
            className='flex w-full flex-col items-center justify-center gap-10 bg-[#2C354C] bg-cover bg-bottom bg-no-repeat py-10 sm:flex-row'
            style={{ backgroundImage: "url('Gibber-Texture.png')" }}
          >
            {/* Logo */}
            <div className='flex w-[280px] flex-col items-center justify-center gap-2.5 md:w-[360px] lg:w-[448px]'>
              <img src='/GradientLogo.svg' className='h-auto w-[200px] md:w-[280px] lg:w-[360px] xl:w-[390px]' alt='gradientlogo' />
            </div>

            {/* Text Content */}
            <div className='flex w-[280px] flex-col items-center justify-center gap-4 text-center md:w-[360px] lg:w-[484px]'>
              <div className='flex flex-col justify-end'>
                <span className='font-syne text-[28px] font-bold leading-tight text-white md:text-[36px] lg:text-[44px] xl:text-[50px]'>
                  Welcome
                  <br />
                </span>
                <span className='font-syne text-[18px] font-bold text-white md:text-[22px] lg:text-[26px] xl:text-[30px]'>to the</span>
              </div>

              <img
                src='/PlaycastPavilionText.svg'
                className='h-auto w-[240px] object-contain md:w-[420px] lg:w-[540px] xl:w-[615px]'
                alt='Playcast Pavilion'
              />

              <div className='font-syne text-[18px] font-bold leading-tight text-white md:text-[22px] lg:text-[26px] xl:text-[30px]'>
                A First-of-its-Kind
                <br />
                Marketplace for Play
              </div>
            </div>
          </div>

        {/* Open Pavilion Banner */}
        <div className='flex w-full flex-col items-stretch justify-between overflow-hidden bg-[#111827] outline outline-[3px] outline-[#111827] xl:flex-row'>
          {/* Left Column with Horizontal Background Fill */}
          <div
            className='flex w-full flex-col justify-center bg-contain bg-left bg-no-repeat px-6 py-6 sm:px-10 lg:px-[60px] xl:w-[45%]'
            style={{ backgroundImage: 'url(/open-pavilion-bg.svg)', backgroundSize: '100% auto' }}
          >
            <div className='font-syne text-[28px] font-bold leading-[1.1] text-[#FFFEFA] sm:text-[36px] md:text-[48px] xl:text-[60px]'>
              Playcast
              <br />
              Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> <br></br> is Open!
            </div>
          </div>

          {/* Right Column */}
          <div className='flex w-full flex-col items-center justify-center px-6 py-6 text-center sm:px-10 xl:w-[55%] xl:px-[60px]'>
            <span className='font-inter max-w-[700px] text-[14px] italic leading-snug text-[#FFFEFA] sm:text-[18px] md:text-[22px] xl:text-[25px]'>
              Join us for the launch of{' '}
              <span className='text-[#FFD219]'>
                Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup>
              </span>
              , a live game-streaming service featuring hundreds of marketing-leading games free to new Hosts and Streamers who join our
              affiliate program.
            </span>
          </div>
        </div>

        {/* Games Carousel */}
        <div className='box-border flex w-full flex-col items-center gap-6 p-10 lg:flex-row'>
          {/* Left Column */}
          <div className='flex h-full w-full items-center justify-center p-6 lg:w-2/5'>
            <div className='font-inter flex h-full w-full flex-col justify-center text-center text-[18px] font-normal italic text-white sm:text-[20px] md:text-[22px] lg:text-left xl:text-[25px]'>
              <p>
                Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> is an app that joins your Windows
                Gaming PC to a globally-distributed, game-streaming service.
              </p>
              <p className='mt-4'>
                Your favorite game developers provide you with free games in exchange for demos, hosting, and promotion by streamers and
                gamers. Join the Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> and get your
                games for free!
              </p>
            </div>
          </div>

          {/* Right Column: Carousel */}
          <div className='relative h-[300px] w-full overflow-hidden sm:h-[360px] md:h-[400px] lg:h-[420px] lg:w-3/5'>
            {/* Edge fades */}
            <div className='pointer-events-none absolute left-0 top-0 z-20 h-full w-[100px] bg-gradient-to-r from-[#111827] to-transparent' />
            <div className='pointer-events-none absolute right-0 top-0 z-20 h-full w-[100px] bg-gradient-to-l from-[#111827] to-transparent' />

            {/* Carousel track */}
            <div
              className='flex'
              style={{
                transition: isTransitioning ? 'transform 6s linear' : 'none',
                transform: `translateX(-${index * (240 + 20)}px)`,
                width: `${clonedImages.length * (240 + 20)}px`,
              }}
            >
              {clonedImages.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`carousel-${i}`}
                  className='mx-[10px] aspect-[3/4] h-[240px] shrink-0 object-cover shadow-[10px_10px_10px_rgba(0,0,0,1)] sm:h-[280px] md:h-[320px] lg:h-[360px] xl:h-[400px]'
                />
              ))}
            </div>
          </div>
        </div>

        {/* Why Join the Pavilion? */}
        <div style={{ width: '100%', backgroundColor: '#2C354C', paddingTop: 40, paddingBottom: 40 }}>
          <div style={{ width: '100%', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
            <div
              style={{
                alignSelf: 'stretch',
                textAlign: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
                color: 'white',
                fontSize: 60,
                fontFamily: 'Syne',
                fontWeight: '700',
                whiteSpace: 'normal',
              }}
            >
              <div className='whitespace-normal'>
                Why Join
                <br />
                Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> ?
              </div>
            </div>
            <div
              style={{
                alignSelf: 'stretch',
                textAlign: 'center',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
                color: 'white',
                fontSize: 25,
                fontFamily: 'Inter',
                fontStyle: 'italic',
                fontWeight: '400',
                wordWrap: 'break-word',
              }}
            >
              We've built a game sharing ecosystem where everyone wins:
            </div>
          </div>
        </div>

        {/* Banners Section */}
        <BannersSection />

        <section id='section-hosts'>
          {/* For Hosts Section */}
          <div className='box-border flex w-screen flex-col items-stretch bg-[#1F273A] pb-[100px] pt-[100px]'>
            {/* Responsive Row: Columns stack on small screens */}
            <div className='flex w-full flex-col lg:flex-row'>
              {/* Left Column — Flush with left edge */}
              <div className='flex flex-1 flex-col items-start gap-4 pb-[50px] pl-0 lg:pb-0'>
                {/* Text content with responsive padding */}
                <div className='w-full pl-8 lg:pl-[140px]'>
                  <div
                    className='text-[50px] lg:text-[60px]'
                    style={{ color: 'white', fontFamily: 'Syne', fontWeight: '700', wordWrap: 'break-word' }}
                  >
                    For Hosts
                  </div>
                  <div style={{ color: '#FFD219', fontSize: 30, fontFamily: 'Inter', fontWeight: '400', wordWrap: 'break-word' }}>
                    Get Access to Free Games.
                    <br />
                    Make Your Machine Work for You.
                  </div>
                </div>

                {/* Image — no padding */}
                <SignedIn>
                  {isMobileDevice() ? (
                    <div className='h-auto w-full'>
                      <img
                        src='/pavilion-left-banner.svg'
                        alt='Playcast Pavilion download'
                        className='block h-auto w-full object-cover opacity-75'
                      />
                    </div>
                  ) : (
                    <div onClick={() => download()} className='h-auto w-full cursor-pointer'>
                      <img src='/pavilion-left-banner.svg' alt='download banner' className='block h-auto w-full object-cover' />
                    </div>
                  )}
                </SignedIn>
                <SignedOut>
                  <SignInButton mode='modal'>
                    <div className='h-auto w-full cursor-pointer' onClick={() => setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS())}>
                      <img src='/pavilion-left-banner.svg' alt='Sign in to download' className='block h-auto w-full object-cover' />
                    </div>
                  </SignInButton>
                </SignedOut>

                {/* "Learn More" with responsive padding */}
                <div className='w-full pl-8 lg:pl-[140px]'>
                  <a
                    href='https://pavilion.playcast.io/a/Playcast'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      color: '#FFFEFA',
                      fontSize: 20,
                      fontFamily: 'Inter',
                      fontWeight: '500',
                      textDecoration: 'underline',
                      wordWrap: 'break-word',
                    }}
                  >
                    Learn More
                  </a>
                </div>
              </div>

              {/* Right Column — Flush right on large, padded left on small */}
              <div className='flex flex-1 flex-col items-start justify-start pl-8 pr-8 lg:items-end lg:pr-0'>
                <div className='w-full lg:w-auto lg:pr-[140px]'>
                  <span
                    style={{
                      color: '#FFFEFA',
                      fontSize: 25,
                      fontFamily: 'Inter',
                      fontStyle: 'italic',
                      fontWeight: '400',
                      wordWrap: 'break-word',
                      textAlign: 'left',
                    }}
                  >
                    Turn your idle Windows gaming PC into a public gaming kiosk, and get your games for free!.
                    <br />
                    <br />
                    You'll love it because:
                  </span>
                  <ul className='mt-4 list-[circle] pl-6'>
                    <li style={{ color: '#FFD219', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Access Hundreds of Free Games
                      <span style={{ color: '#FFFEFA' }}>&nbsp;by gaining access to our large game cafe, including top AAA titles.</span>
                    </li>
                    <li style={{ color: '#FFD219', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Play Steam titles
                      <span style={{ color: '#FFFEFA' }}>&nbsp;—even if you don't own them</span>
                    </li>
                    <li style={{ color: '#FFD219', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Host your own live-streaming service
                      <span style={{ color: '#FFFEFA' }}>&nbsp;for friends and family</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id='section-streamers'>
          {/* Streamers Section */}
          <div className='box-border flex w-screen flex-col items-stretch bg-[#111827] pb-[100px] pt-[100px]'>
            {/* Responsive Row: Right column stacks on top of left when narrow */}
            <div className='flex w-full flex-col-reverse lg:flex-row'>
              {/* Left Column (Text List) */}
              <div className='flex flex-1 flex-col items-start gap-4 pl-0'>
                <div className='w-full pl-8 pr-8 lg:pl-[140px] lg:pr-0'>
                  <span
                    style={{
                      color: '#FFFEFA',
                      fontSize: 25,
                      fontFamily: 'Inter',
                      fontStyle: 'italic',
                      fontWeight: '400',
                      wordWrap: 'break-word',
                    }}
                  >
                    Play games with your audiences live-on stream from a free catalog of hundreds of leading games.
                    <br />
                    <br />
                    You'll love it because:
                  </span>
                  <ul className='mt-4 list-[circle] space-y-[10px] pl-6'>
                    <li style={{ color: '#C084FC', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Play hundreds of Steam titles
                      <span style={{ color: '#FFFEFA' }}>&nbsp;— even if you don't own them</span>
                    </li>
                    <li style={{ color: '#C084FC', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Become a Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> Affiliate
                      <span style={{ color: '#FFFEFA' }}>&nbsp;and earn rewards for recruiting hosts to support your channel.</span>
                    </li>
                    <li style={{ color: '#C084FC', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Host sponsored events
                      <span style={{ color: '#FFFEFA' }}>&nbsp;and share prizes with your biggest supporters</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Column (Heading + Image) */}
              <div className='flex flex-1 flex-col items-start justify-start pb-[50px] pl-8 lg:items-end lg:pb-0 lg:pl-0'>
                <div className='w-full space-y-4 pr-8 text-right lg:w-auto lg:pr-[140px]'>
                  <div
                    className='text-[50px] lg:text-[60px]'
                    style={{ color: 'white', fontFamily: 'Syne', fontWeight: '700', lineHeight: 1 }}
                  >
                    For Streamers
                  </div>
                  <div style={{ color: '#C084FC', fontSize: 30, fontFamily: 'Inter', fontWeight: '400', wordWrap: 'break-word' }}>
                    Your Steam,
                    <br />
                    Upgraded to Real-Time Play
                  </div>
                </div>
                <div className='w-full pt-[100px]'>
                  <a href='https://forms.gle/f8L8Z6J1gdseQfAb6' target='_blank' rel='noopener noreferrer' className='w-full pt-[100px]'>
                    <img src='/streamer-right.svg' alt='streamer-right' className='block h-auto w-full object-cover' />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id='section-gamers'>
          {/* Gamers Section */}
          <div className='box-border flex w-screen flex-col items-stretch bg-[#1F273A] pb-[100px] pt-[100px]'>
            {/* Responsive Row: Columns stack on small screens */}
            <div className='flex w-full flex-col lg:flex-row'>
              {/* Left Column — Title + Image */}
              <div className='flex flex-1 flex-col items-start gap-4 pb-[50px] pl-0 lg:pb-0'>
                {/* Title Block with responsive right padding */}
                <div className='w-full pl-8 pr-8 text-left lg:pr-[140px]'>
                  <div className='text-[50px] lg:text-[60px]' style={{ color: 'white', fontFamily: 'Syne', fontWeight: '700' }}>
                    For Gamers
                  </div>
                  <div style={{ color: '#FFD219', fontSize: 30, fontFamily: 'Inter', fontWeight: '400', marginTop: 20 }}>
                    Play for Free.
                    <br />
                    No Downloads.
                    <br />
                    Play from Anywhere.
                  </div>
                </div>

                {/* Image block without padding */}
                <div className='mt-auto w-full'>
                  <a href='https://pavilion.playcast.io/p/playcast/' target='_blank' rel='noopener noreferrer'>
                    <img src='/gamer-banner.svg' alt='gamer-banner' className='block h-auto w-full object-cover' />
                  </a>
                </div>
              </div>

              {/* Right Column — Paragraph and List */}
              <div className='flex flex-1 flex-col items-start justify-start pl-8 pr-8 lg:pl-[140px] lg:pr-0'>
                <div className='w-full'>
                  <span
                    style={{
                      color: '#FFFEFA',
                      fontSize: 25,
                      fontFamily: 'Inter',
                      fontStyle: 'italic',
                      fontWeight: '400',
                      wordWrap: 'break-word',
                    }}
                  >
                    No gaming PC? No problem. Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup>{' '}
                    gives you instant access to top-tier PC games for free—right from your browser on any device.
                    <br />
                    <br />
                    You'll love it because:
                  </span>

                  <ul className='mt-4 list-[circle] space-y-[10px] pl-6'>
                    <li style={{ color: '#FFD219', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      100% free to play
                      <span style={{ color: '#FFFEFA' }}>&nbsp;— no installs, subscriptions, or game ownership</span>
                    </li>
                    <li style={{ color: '#FFD219', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Play games instantly
                      <span style={{ color: '#FFFEFA' }}>
                        &nbsp;from Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> Kiosks
                      </span>
                    </li>
                    <li style={{ color: '#FFD219', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Join streamers or friends
                      <span style={{ color: '#FFFEFA' }}>&nbsp;in real-time game lobbies</span>
                    </li>
                    <li style={{ color: '#FFD219', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Try new games
                      <span style={{ color: '#FFFEFA' }}>&nbsp;before buying</span>
                    </li>
                    <li style={{ color: '#FFD219', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Low-latency connection
                      <span style={{ color: '#FFFEFA' }}>&nbsp;to the nearest host kiosk, anywhere in the world</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id='section-devs'>
          {/* Developers Section */}
          <div className='box-border flex w-screen flex-col items-stretch bg-[#111827] pb-[100px] pt-[100px]'>
            {/* Responsive Row: Right column stacks on top of left when narrow */}
            <div className='flex w-full flex-col-reverse lg:flex-row'>
              {/* Left Column — Paragraph + List */}
              <div className='flex flex-1 flex-col items-start gap-4 pb-[50px] pl-8 pr-8 lg:pb-0 lg:pl-[140px] lg:pr-0'>
                <div className='w-full'>
                  <span style={{ color: '#FFFEFA', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                    Promote your game via streamers and spectators on leading streaming platforms
                    <br />
                    <br />
                    You'll love it because:
                  </span>
                  <ul className='mt-4 list-[circle] space-y-[10px] pl-6'>
                    <li style={{ color: '#C084FC', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Let anyone play your demo instantly
                      <span style={{ color: '#FFFEFA' }}>&nbsp;—zero installs or accounts</span>
                    </li>
                    <li style={{ color: '#C084FC', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Gain exposure
                      <span style={{ color: '#FFFEFA' }}>
                        &nbsp;alongside streamers and players in live Playcast Pavilion
                        <sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> stations
                      </span>
                    </li>
                    <li style={{ color: '#C084FC', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      License titles for public promotion
                      <span style={{ color: '#FFFEFA' }}>&nbsp;via Steam Café or distribute promotional keys</span>
                    </li>
                    <li style={{ color: '#C084FC', fontSize: 25, fontFamily: 'Inter', fontStyle: 'italic', fontWeight: '400' }}>
                      Drive Steam purchases
                      <span style={{ color: '#FFFEFA' }}>&nbsp;straight from live browser-based gameplay exposure</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right Column — Heading + CTA + Banner */}
              <div className='flex flex-1 flex-col items-start justify-start pb-[50px] lg:items-end lg:pb-0'>
                {/* Text content with padding */}
                <div className='w-full space-y-6 pl-8 pr-8 text-right lg:w-auto lg:pl-0 lg:pr-[140px]'>
                  <div
                    className='text-[50px] lg:text-[60px]'
                    style={{ color: 'white', fontFamily: 'Syne', fontWeight: '700', lineHeight: 1 }}
                  >
                    For Developers
                  </div>
                  <div style={{ color: '#C084FC', fontSize: 30, fontFamily: 'Inter', fontWeight: '400' }}>
                    Promote your Steam games virally across social media with no download, install, or authentication friction
                  </div>
                  <div className='space-y-2 text-left lg:w-[60%]'>
                    <div style={{ color: 'white', fontSize: 30, fontFamily: 'Inter', fontWeight: '700' }}>How to Join:</div>
                    <div style={{ fontSize: 20, fontFamily: 'Inter', fontWeight: '500', color: 'white' }}>
                      Register your game in <span style={{ color: '#9656CE' }}>Steam Café</span>, then notify us—it's that simple.
                      <br />
                      <br />
                      <span style={{ color: '#9CA3AF' }}>
                        Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup> will buy at least 5
                        commercial licenses for every qualifying title.
                      </span>
                    </div>
                  </div>
                </div>

                {/* Banner — flush with screen edge */}
                <div className='w-full pt-[20px]'>
                  <a href='https://forms.gle/5rTn5yiU5jDfLdxG8' target='_blank' rel='noopener noreferrer'>
                    <img src='/game-submit-banner.png' alt='game-submit-banner' className='block h-auto w-full object-cover' />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Section */}
        <div
          className='flex w-full flex-col items-center justify-center gap-12 overflow-hidden bg-[#2C354C] pb-[84px] pt-[84px]'
          style={{
            backgroundImage: "url('/Gibber-Texture.png')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% auto',
            backgroundPosition: 'bottom',
          }}
        >
          {/* Title + Description */}
          <div className='flex w-4/5 flex-col items-center space-y-6 text-center lg:w-3/5'>
            <div className='text-[40px] text-white lg:text-[60px]' style={{ fontFamily: 'Syne', fontWeight: '700' }}>
              Playcast Pavilion<sup className='text-decoration-none align-super text-[0.35em]'>TM</sup>
              <br />
              Is Open
            </div>
            <div className='text-[20px] text-white lg:text-[30px]' style={{ fontFamily: 'Inter', fontWeight: '400' }}>
              Whether you want to showcase your game, grow your stream, support creators, or just start playing — now's the time to join.
            </div>
          </div>

          {/* Button Row - stacks on mobile */}
          <div className='flex w-full flex-col items-center justify-center gap-4 px-8 sm:w-auto sm:flex-row'>
            <a href={config.DISCORD_URL} target='_blank' rel='noopener noreferrer' className='w-full sm:w-auto'>
              <img src='/discordbutton.svg' alt='discordbutton' className='h-auto w-full object-cover' />
            </a>

            <SignedIn>
              {isMobileDevice() || isNonWindowsOS() ? (
                <div className='w-full sm:w-auto'>
                  <img src='/download footer.svg' alt='Playcast Pavilion download' className='h-auto w-full object-cover opacity-75' />
                </div>
              ) : (
                <div onClick={() => download()} className='w-full cursor-pointer sm:w-auto'>
                  <img src='/download footer.svg' alt='download footer' className='h-auto w-full object-cover' />
                </div>
              )}
            </SignedIn>

            <SignedOut>
              <SignInButton mode='modal'>
                <div className='w-full cursor-pointer sm:w-auto' onClick={() => setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS())}>
                  <img src='/download footer.svg' alt='Sign in to download' className='h-auto w-full object-cover' />
                </div>
              </SignInButton>
            </SignedOut>
          </div>
        </div>
      </div>
    </>
  ) : (
    <>
      <Helmet>
        <title>Playcast - Get Games For Free</title>
        <meta
          name='description'
          content='Become part of the Playcast Network, reap rewards, and support play while your PC is idle. Get games for free!'
        />
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* New Aida Design Components */}
      <div
        className='w-full'
        style={{
          backgroundColor: 'black',
          margin: 0,
          padding: 0,
          width: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Content wrapper with padding for header */}
        <div style={{
          paddingTop: '60px',
          display: 'flex',
          justifyContent: 'center',
          margin: 0,
          padding: 0
        }}>
          {/* Responsive scaling wrapper - Only scales content, NOT footer */}
          <div
            id="auto-scaler"
            style={{
              width: '1024px',
              transformOrigin: 'top center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              margin: 0,
              padding: 0,
              marginBottom: 0,
              overflow: 'visible',
              opacity: isContentReady ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            
            {/* Purple Nebula Background Zone */}
            <div style={{
              width: '100%',
              background: [
                'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0.2) 25%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.2) 75%, rgba(0,0,0,0.85) 90%, rgba(0,0,0,1) 100%)',
                'linear-gradient(90deg, rgba(120,40,255,0.55), rgba(180,60,255,0.55))',
                'radial-gradient(1400px at 50% 40%, rgba(255,255,255,0.03), rgba(60,0,90,0) 75%)',
                'radial-gradient(1000px at 15% 35%, rgba(140,40,255,0.40), rgba(40,0,70,0) 75%)',
                'radial-gradient(900px at 55% 60%, rgba(160,50,255,0.30), rgba(35,0,60,0) 80%)',
                'radial-gradient(1000px at 85% 70%, rgba(200,70,255,0.40), rgba(45,0,70,0) 80%)',
                'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 60%)',
                'radial-gradient(circle at 50% 25%, #2a0050 0%, #1a0038 35%, #000000 75%, #000000 100%)',
              ].join(', '),
              backgroundBlendMode: 'normal, soft-light, overlay, screen, screen, screen, soft-light, multiply',
            }}>
              <div style={{ width: '100%', marginTop: '40px' }}>
                <RWBYCarousel onActiveDemoUrl={setActiveDemoUrl} />
              </div>
              <div style={{ width: '100%' }}>
                <HomeThreePlatformOptions activeDemoUrl={activeDemoUrl} />
              </div>
            </div>

            {/* Nebula Violet Background Zone */}
            <div style={{
              width: '100%',
              background: [
                'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0.2) 25%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.2) 75%, rgba(0,0,0,0.85) 90%, rgba(0,0,0,1) 100%)',
                'linear-gradient(90deg, rgba(120,40,255,0.55), rgba(180,60,255,0.55))',
                'radial-gradient(1400px at 50% 40%, rgba(255,255,255,0.03), rgba(60,0,90,0) 75%)',
                'radial-gradient(1000px at 15% 35%, rgba(140,40,255,0.40), rgba(40,0,70,0) 75%)',
                'radial-gradient(900px at 55% 60%, rgba(160,50,255,0.30), rgba(35,0,60,0) 80%)',
                'radial-gradient(1000px at 85% 70%, rgba(200,70,255,0.40), rgba(45,0,70,0) 80%)',
                'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 60%)',
                'radial-gradient(circle at 50% 25%, #2a0050 0%, #1a0038 35%, #000000 75%, #000000 100%)',
              ].join(', '),
              backgroundBlendMode: 'normal, soft-light, overlay, screen, screen, screen, soft-light, multiply',
            }}>
              <div style={{ width: '100%', marginTop: '40px' }}>
                <InstantPlayPillars />
              </div>
              <div style={{ width: '100%', marginTop: '40px' }}>
                <SecurityControl />
              </div>
            </div>



            {/* Trusted Partners Reel */}
            <div style={{ width: '100%', marginTop: '40px' }}>
              <TrustedPartnersReel />
            </div>

            {/* FAQ */}
            <div style={{ width: '100%', marginTop: '40px' }}>
              <FAQ />
            </div>

            {/* Final CTA */}
            <div style={{ width: '100%', marginTop: '40px' }}>
              <FinalCTA />
            </div>

            {/* Hero Section */}
            {/* <div style={{ width: '100%', marginTop: '40px' }}>
              <AidaHero />
            </div> */}

            {/* Featured Game Section */}
            {/* <div style={{ width: '100%', marginTop: '40px' }}>
              <AidaFeaturedGame />
            </div> */}


            {/* Feature Highlights Section */}
            {/* <div style={{ width: '100%', marginTop: '40px' }}>
              <AidaFeatureHighlights />
            </div> */}

            {/* FAQ Section */}
            {/* <div id="faq-section" style={{ width: '100%', marginTop: '40px' }}>
              <AidaFAQ />
            </div> */}

            {/* Final CTA Section */}
            {/* <div style={{ width: '100%', marginTop: '40px' }}>
              <AidaFinalCTA />
            </div> */}
          </div>
        </div>

        {/* Footer - Outside of scaling wrapper */}
        <div style={{ width: '100%', margin: 0, marginTop: 0, padding: 0 }}>
          <AidaFooter />
        </div>
      </div>
    </>
  );
};
